import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import { debounce } from 'patronum';
import { $activeInstruments } from '../../entities/instrument';
import { $watchlist } from '../../entities/market';
import { openPositionFx, positionSelected } from '../../entities/position';
import { $locale, $preferences } from '../../entities/preferences';
import { toastShown } from '../../entities/toast';
import { api, ApiError, unwrap, type Schemas } from '../../shared/api/client';
import { parseDecimal, roundToTick } from '../../shared/lib/decimal';
import { i18n } from '../../shared/lib/i18n';
import { latest } from '../../shared/lib/latest';

export interface Draft {
  instrumentId: string;
  price: string;
  initialSum: string;
  target: string;
  levels: string;
  step: string;
  multiplier: string;
}

export type DraftField = keyof Draft;

const EMPTY: Draft = {
  instrumentId: '',
  price: '',
  initialSum: '',
  target: '5',
  levels: '4',
  step: '10',
  multiplier: '2',
};

export interface OpenOptions {
  instrumentId?: number;
}

export const dialogOpened = createEvent<OpenOptions>();
export const dialogClosed = createEvent();
export const fieldChanged = createEvent<{ field: DraftField; value: string }>();
export const advancedToggled = createEvent();
export const submitted = createEvent();

export const $open = createStore(false)
  .on(dialogOpened, () => true)
  .on(dialogClosed, () => false)
  .on(openPositionFx.done, () => false);

export const $advanced = createStore(false)
  .on(advancedToggled, (open) => !open)
  .reset(dialogOpened);

export const $draft = createStore<Draft>(EMPTY).on(fieldChanged, (draft, { field, value }) => ({
  ...draft,
  [field]: value,
}));

/** Latest quote for an instrument, formatted for the input in the current language. */
function priceFor(
  watchlist: Schemas['WatchItemOut'][] | null,
  instrumentId: number,
  locale: 'en' | 'bg',
): string {
  const item = watchlist?.find((w) => w.instrument.id === instrumentId);
  if (!item?.quote) return '';
  const price = roundToTick(item.quote.price, item.instrument.tick_size);
  return locale === 'bg' ? price.replace('.', ',') : price;
}

// Opening resets the draft from the owner's defaults and the latest quote.
sample({
  clock: dialogOpened,
  source: {
    prefs: $preferences,
    instruments: $activeInstruments,
    watchlist: $watchlist,
    locale: $locale,
  },
  fn: ({ prefs, instruments, watchlist, locale }, options): Draft => {
    const id = options.instrumentId ?? instruments[0]?.id;
    const decimal = (value: string | undefined, fallback: string): string => {
      const text = value ?? fallback;
      return locale === 'bg' ? text.replace('.', ',') : text;
    };
    return {
      instrumentId: id === undefined ? '' : String(id),
      price: id === undefined ? '' : priceFor(watchlist, id, locale),
      initialSum: '',
      target: decimal(prefs?.default_target_pct, EMPTY.target),
      levels: String(prefs?.default_levels ?? EMPTY.levels),
      step: decimal(prefs?.default_step_pct, EMPTY.step),
      multiplier: decimal(prefs?.default_multiplier, EMPTY.multiplier),
    };
  },
  target: $draft,
});

// Picking another instrument suggests its latest price.
sample({
  clock: fieldChanged,
  source: { draft: $draft, watchlist: $watchlist, locale: $locale },
  filter: (_, { field }) => field === 'instrumentId',
  fn: ({ draft, watchlist, locale }) => ({
    ...draft,
    price: priceFor(watchlist, Number(draft.instrumentId), locale),
  }),
  target: $draft,
});

// Instruments or quotes that arrive after the dialog opened fill whatever is still empty.
sample({
  clock: [$activeInstruments.updates, $watchlist.updates],
  source: {
    open: $open,
    draft: $draft,
    instruments: $activeInstruments,
    watchlist: $watchlist,
    locale: $locale,
  },
  filter: ({ open, draft }) => open && (draft.instrumentId === '' || draft.price === ''),
  fn: ({ draft, instruments, watchlist, locale }): Draft => {
    const instrumentId = draft.instrumentId || String(instruments[0]?.id ?? '');
    return {
      ...draft,
      instrumentId,
      price: draft.price || (instrumentId ? priceFor(watchlist, Number(instrumentId), locale) : ''),
    };
  },
  target: $draft,
});

export interface Parsed {
  body: Schemas['LadderIn'] | null;
  errors: Partial<Record<DraftField, string>>;
}

export function parseDraft(draft: Draft, locale: 'en' | 'bg'): Parsed {
  const errors: Partial<Record<DraftField, string>> = {};
  const number = (field: DraftField): string | null => {
    const value = parseDecimal(draft[field], locale);
    if (draft[field].trim() !== '' && value === null) errors[field] = i18n.t('form.notANumber');
    return value;
  };
  const price = number('price');
  const initialSum = number('initialSum');
  const target = number('target');
  const step = number('step');
  const multiplier = number('multiplier');
  const levels = Number.parseInt(draft.levels, 10);
  if (!draft.instrumentId || price === null || initialSum === null || target === null) {
    return { body: null, errors };
  }
  if (step === null || multiplier === null || Number.isNaN(levels)) return { body: null, errors };
  return {
    body: {
      instrument_id: Number(draft.instrumentId),
      entry_price: price,
      initial_sum: initialSum,
      target_pct: target,
      levels,
      step_pct: step,
      multiplier,
    },
    errors,
  };
}

export const $parsed = combine($draft, $locale, parseDraft);

// --- Live preview ---------------------------------------------------------------------------

export const previewFx = createEffect((body: Schemas['LadderIn']) =>
  unwrap(api.POST('/api/v1/ladder/preview', { body })),
);

export const analogsFx = createEffect((body: Schemas['LadderIn']) =>
  unwrap(
    api.GET('/api/v1/regime/analogs', {
      params: {
        query: {
          instrument_id: body.instrument_id,
          levels: body.levels,
          step_pct: Number(body.step_pct),
          multiplier: Number(body.multiplier),
          target_pct: Number(body.target_pct),
        },
      },
    }),
  ),
);

const previewWanted = debounce($parsed.updates, 150);
const analogsWanted = debounce($parsed.updates, 400);
// Answers can arrive out of order; only the latest input's answer is shown.
const preview = latest(previewFx, { reset: dialogOpened });
const analogs = latest(analogsFx, { reset: dialogOpened });

sample({
  clock: previewWanted,
  source: $open,
  filter: (open, parsed) => open && parsed.body !== null,
  fn: (_, parsed) => parsed.body as Schemas['LadderIn'],
  target: preview.run,
});

const analogKey = (body: Schemas['LadderIn']): string =>
  [body.instrument_id, body.levels, body.step_pct, body.multiplier, body.target_pct].join('|');

// The inputs last asked about, so unchanged inputs aren't asked again; a failure allows a retry.
const $analogKey = createStore('')
  .on(analogsFx, (_, body) => analogKey(body))
  .reset(analogsFx.fail, dialogOpened);

sample({
  clock: analogsWanted,
  source: { open: $open, key: $analogKey },
  filter: ({ open, key }, parsed) => open && parsed.body !== null && analogKey(parsed.body) !== key,
  fn: (_, parsed) => parsed.body as Schemas['LadderIn'],
  target: analogs.run,
});

// Keep the last good preview on screen while a new one is in flight (CLAUDE.md §1.2).
export const $preview = createStore<Schemas['LadderPreviewOut'] | null>(null)
  .on(preview.done, (_, result) => result)
  .reset(dialogOpened);

export const $serverErrors = createStore<Record<string, string>>({})
  .on(preview.done, () => ({}))
  .on(preview.fail, (_, error) => (error instanceof ApiError ? error.fields : {}))
  .on(openPositionFx.failData, (_, error) => (error instanceof ApiError ? error.fields : {}))
  .reset(dialogOpened);

export const $analogs = createStore<Schemas['AnalogsOut'] | null>(null)
  .on(analogs.done, (_, result) => result)
  .reset(dialogOpened);

/** Field errors: local parsing first, then what the server said about the same field. */
export const $errors = combine($parsed, $serverErrors, (parsed, server) => {
  const map: Partial<Record<DraftField, string>> = { ...parsed.errors };
  const fields: Record<string, DraftField> = {
    instrument_id: 'instrumentId',
    entry_price: 'price',
    initial_sum: 'initialSum',
    target_pct: 'target',
    levels: 'levels',
    step_pct: 'step',
    multiplier: 'multiplier',
  };
  for (const [key, message] of Object.entries(server)) {
    const field = fields[key];
    if (field && !map[field]) map[field] = message;
  }
  return map;
});

// --- Submit ----------------------------------------------------------------------------------

export const $submitAttempted = createStore(false)
  .on(submitted, () => true)
  .reset(dialogOpened);

sample({
  clock: submitted,
  source: { parsed: $parsed, pending: openPositionFx.pending },
  filter: ({ parsed, pending }) => parsed.body !== null && !pending,
  fn: ({ parsed }) => parsed.body as Schemas['PositionIn'],
  target: openPositionFx,
});

sample({ clock: openPositionFx.doneData, fn: (position) => position.id, target: positionSelected });

sample({
  clock: openPositionFx.doneData,
  fn: (position) => ({
    message: i18n.t('openPosition.opened', { symbol: position.instrument.display_symbol }),
    tone: 'neutral' as const,
  }),
  target: toastShown,
});

sample({
  clock: openPositionFx.failData,
  filter: (error) => !(error instanceof ApiError) || Object.keys(error.fields).length === 0,
  fn: (error) => ({ message: error.message, tone: 'error' as const }),
  target: toastShown,
});
